/**
 * CSV Export utility for Hybrid Timescale Reporting downloads.
 *
 * Generates RFC 4180 compliant CSV files from reporting data and triggers
 * browser download. Uses human-readable column headers matching the previous
 * reporting format (e.g. "Start date", "Average power (kW)").
 *
 * Promoted from the systems section (`csvExport.ts`) as the shared download CSV
 * implementation for the timescale REST path. This is distinct from the legacy
 * `usageCsv.ts` (RowType-based, non-timescale path); one implementation per path.
 */

import { FIELD_GROUPS } from "./fieldGroups"

/**
 * Columns the export carries alongside a selected field without offering them
 * in the field selector.
 *
 * Cross-repo contract:
 * Must stay in step with `SETTLED_COLUMNS` in `core`:
 *   core/logiq/api/timeseries/timeseries/reporting/fuel_display.py
 * Any companion column served by the backend must have its matching entry here
 * and in CSV_HEADINGS below. Drift causes a CSV column with no header or an
 * empty companion column.
 *
 * `diesel_fuel_consumed` is the signed measurement: a bucket holds the fall in
 * tank level since the previous one, so movement within the sender's tolerance
 * leaves small negatives and a window telescopes to the real drawdown. Those
 * negatives are what an analyst needs and what a chart cannot draw, so the
 * backend also emits the same series with them settled into their neighbours.
 *
 * Both are exported together. Per bucket they differ; over the window they come
 * to the same figure. Anyone reconciling a chart against a spreadsheet can see
 * where and why they part company, which is not possible if a surface silently
 * picks one.
 *
 * The settled column is a presentation of a measurement, not a second
 * measurement: it has no inverse, and one of its values will not tie back to a
 * single database row.
 */
const COMPANION_FIELDS: Record<string, string> = {
  diesel_fuel_consumed: "diesel_fuel_consumed_displayed",
}

/** Metadata about the current report, used for filename */
export interface ReportingMeta {
  system_id: string
  system_name: string
  site_id?: string
  start: string
  stop: string
  frequency: string
  timezone: string
}

/**
 * Map from Display_Field_Key to human-readable CSV column header.
 * Consistent with table headers and field selector labels.
 */
const CSV_HEADINGS: Record<string, string> = {
  // Period
  bucket_start: "Start date",
  bucket_end: "End date",

  // Power Generated
  generation_power_average: "Generated average power (kW)",
  generation_power_peak: "Generated peak power (kW)",
  generation_power_peak_time: "Generated peak time",
  generation_energy: "Generated energy (kWh)",
  generation_co2e: "CO2e total (kg)",
  generation_co2e_per_kwh: "CO2e (kg/kWh)",

  // Power Consumed
  load_power_average: "Consumed average power (kW)",
  load_power_peak: "Consumed peak power (kW)",
  load_power_peak_time: "Consumed peak time",
  load_energy: "Consumed energy (kWh)",

  // Hydrogen
  hydrogen_weight_start: "H2 start weight (kg)",
  hydrogen_weight_end: "H2 end weight (kg)",
  hydrogen_weight_consumed: "H2 weight consumed (kg)",

  // Diesel
  diesel_level: "Diesel average level (%)",
  diesel_level_start: "Diesel start level (L)",
  diesel_level_end: "Diesel end level (L)",
  diesel_fuel_consumed: "Diesel fuel consumed (L)",
  diesel_fuel_consumed_displayed: "Diesel fuel consumed, as charted (L)",

  // Water and waste (Non-Fuel Storage)
  water_level: "Water average level (%)",
  waste_level: "Waste average level (%)",

  // Energy Storage
  battery_soc_average: "Battery average SoC (%)",
  battery_soc_peak: "Battery max SoC (%)",
  battery_soc_min: "Battery min SoC (%)",
  battery_discharge_power_average: "Battery discharge power (kW)",
  battery_discharge_energy: "Battery discharge energy (kWh)",
  battery_charge_power_average: "Battery charge power (kW)",
  battery_charge_energy: "Battery charge energy (kWh)",
}

/**
 * Get the human-readable CSV column header for a field key.
 */
function getHeading(fieldKey: string): string {
  return CSV_HEADINGS[fieldKey] ?? fieldKey
}

/**
 * Escapes a CSV field value per RFC 4180:
 * - Fields containing commas, double quotes, or newlines must be enclosed in double quotes
 * - Double quotes within a field are escaped by doubling them
 */
function escapeField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n") || value.includes("\r")) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/**
 * Formats a cell value for CSV output:
 * - null/undefined → empty string (empty cell)
 * - Numbers → 2 decimal places, `.` decimal separator, no thousands separator
 * - Strings → escaped per RFC 4180
 */
function formatCellValue(value: unknown): string {
  // No value is an empty cell, never 0: a level with no reading is not 0%.
  if (value === null || value === undefined) {
    return ""
  }

  if (typeof value === "number") {
    return escapeField(value.toFixed(2))
  }

  return escapeField(String(value))
}

/**
 * Generates the CSV filename following the pattern:
 * {system_name}_{start_date}_{end_date}_{frequency}.csv
 * Spaces replaced with underscores, timestamps trimmed to date only.
 */
export function generateFilename(meta: ReportingMeta): string {
  const name = meta.system_name.replace(/\s+/g, "_")
  const start = meta.start.split("T")[0]
  const stop = meta.stop.split("T")[0]
  return `${name}_${start}_${stop}_${meta.frequency}.csv`
}

/**
 * Generates CSV content as a string without triggering download.
 *
 * Column order:
 * 1. "Start date", "End date" (period columns)
 * 2. Selected field columns with human-readable headers
 *
 * Format:
 * - UTF-8, RFC 4180 compliant
 * - ISO-8601 timestamps (with timezone offset)
 * - Empty cells for nulls
 * - `.` decimal separator, no thousands separator
 * - CRLF line endings per RFC 4180
 */
export function generateCsvContent(
  rows: Record<string, unknown>[],
  selectedFields: string[],
): string {
  // Order selected fields by canonical group order (same as table)
  const canonicalOrder = FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key))
  const companionValues = new Set(Object.values(COMPANION_FIELDS))
  const orderedFields = selectedFields
    .filter((f) => f !== "bucket_start" && f !== "bucket_end" && !companionValues.has(f))
    .sort((a, b) => {
      const idxA = canonicalOrder.indexOf(a)
      const idxB = canonicalOrder.indexOf(b)
      const orderA = idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA
      const orderB = idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB
      return orderA - orderB
    })

  // All columns: period + ordered selected fields, each followed by any
  // companion it carries, so the pair reads side by side in the spreadsheet.
  const withCompanions = orderedFields.flatMap((field) => {
    const companion = COMPANION_FIELDS[field]
    return companion ? [field, companion] : [field]
  })
  const allFieldKeys = ["bucket_start", "bucket_end", ...withCompanions]

  // Build header row with human-readable names
  const headerRow = allFieldKeys.map((key) => escapeField(getHeading(key))).join(",")

  // Build data rows
  const dataRows = rows.map((row) => {
    return allFieldKeys.map((key) => formatCellValue(row[key] ?? null)).join(",")
  })

  // Join with CRLF per RFC 4180
  return [headerRow, ...dataRows].join("\r\n")
}

/**
 * Generates a CSV file from reporting data and triggers a browser download.
 *
 * @param rows - Array of data rows from the reporting API
 * @param selectedFields - Array of selected Display_Field_Key column names (excluding period)
 * @param meta - Report metadata for filename
 */
export function exportCsv(
  rows: Record<string, unknown>[],
  selectedFields: string[],
  meta: ReportingMeta
): void {
  const csvContent = generateCsvContent(rows, selectedFields)
  const filename = generateFilename(meta)

  // Create UTF-8 encoded Blob with BOM for Excel compatibility
  const BOM = "\uFEFF"
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" })

  // Trigger download via anchor element
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.setAttribute("href", url)
  link.setAttribute("download", filename)
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
